import {
    Box,
    Button,
    Grid,
    GridSize,
    IconButton,
    Typography,
} from '@material-ui/core';
import { usePutFatura } from 'data/api/gestao/faturas/put-fatura';
import { FaturaFormModel } from 'model/app/forms/fatura/fatura-form-model';
import { EnumTipoPessoas } from 'model/enums/enum-tipo-pessoas';
import { EnumTipoFatura } from 'model/enums/enum-tipo-fatura';
import { useCallback, useEffect, useState } from 'react';
import { useSessaoAtual, useToastSaurus } from 'services/app';
import classNames from 'classnames';
import { toDateString } from 'utils/to-date';
import { toDecimal, toDecimalString } from 'utils/to-decimal';
import { toCurrency } from 'utils/toCurrency';
import { TextFieldSaurus } from 'views/components/controles/inputs/text-field-saurus/text-field-saurus';
import { EditarIcon } from 'views/components/icons/editar-icon';
import { useStyles } from './cliente-fatura-styles';
import { CancelarIcon, OkIcon } from 'views/components/icons';

interface FaturaValoresProps {
    model: FaturaFormModel;
    formaPagamentoNome: string;
    tipoFatura: EnumTipoFatura;
    /** Após PUT do custo com sucesso (ex.: recarregar fatura na página). */
    onCustoAtualizado?: () => void | Promise<void>;
}

interface ItemProps {
    label: string;
    conteudo: string;
    traço?: boolean;
    xs?: GridSize;
}

export default function FaturaValores({
    model,
    formaPagamentoNome,
    tipoFatura,
    onCustoAtualizado,
}: FaturaValoresProps) {
    const { tipoUsuario } = useSessaoAtual();
    const { showToast } = useToastSaurus();
    const { putFatura, carregando } = usePutFatura();

    const isLicenca = tipoFatura === EnumTipoFatura.Licença;
    const t = tipoUsuario();
    const podeEditarCusto =
        isLicenca &&
        (t === EnumTipoPessoas.SoftwareHouse ||
            t === EnumTipoPessoas.FinanceiroSoftwareHouse);

    const exibirLinhaCusto =
        t === EnumTipoPessoas.SoftwareHouse ||
        t === EnumTipoPessoas.FinanceiroSoftwareHouse ||
        t === EnumTipoPessoas.Revendedor;

    const [editandoCusto, setEditandoCusto] = useState(false);
    const [valorCustoDraft, setValorCustoDraft] = useState<string>(
        toDecimalString(model.valorCusto),
    );

    useEffect(() => {
        setValorCustoDraft(toDecimalString(model.valorCusto));
    }, [model.valorCusto]);

    const salvarCusto = useCallback(async () => {
        const v = toDecimal(valorCustoDraft);
        const res = await putFatura({
            id: model.id,
            nossoNumero: model.nossoNumero,
            dataVencimento: model.dataVencimento,
            valorCusto: v,
            encargo: model.encargo,
            multaJuros: model.multaJuros,
            valor: model.valor,
        } as FaturaFormModel);

        if (res.erro) {
            showToast('error', res.erro.message ?? 'Erro ao atualizar custo.');
            return;
        }

        showToast('success', 'Valor de custo atualizado!');
        setEditandoCusto(false);
        await onCustoAtualizado?.();
    }, [
        model,
        onCustoAtualizado,
        putFatura,
        showToast,
        valorCustoDraft,
    ]);

    const iniciarEdicaoCusto = useCallback(() => {
        setValorCustoDraft(toDecimalString(model.valorCusto));
        setEditandoCusto(true);
    }, [model.valorCusto]);

    const cancelarEdicaoCusto = useCallback(() => {
        setValorCustoDraft(toDecimalString(model.valorCusto));
        setEditandoCusto(false);
    }, [model.valorCusto]);

    return (
        <Box>
            <Grid container spacing={1}>
                <Item label="Número da Fatura" conteudo={model.nossoNumero.toString()} />
                {!isLicenca && (
                    <Item
                        label="Forma de Pagamento"
                        conteudo={formaPagamentoNome}
                    />
                )}
                <Item
                    label="Data de Vencimento"
                    conteudo={toDateString(model.dataVencimento)!}
                />
                {!isLicenca && (
                    <>
                        <Item label="Encargo" conteudo={toCurrency(model.encargo).toString()} />
                        <Item
                            label="Multa e Juros"
                            conteudo={toCurrency(model.multaJuros).toString()}
                        />
                        <Item
                            label="Valor"
                            conteudo={toCurrency(model.valor).toString()}
                            xs={6}
                        />
                    </>
                )}
                {exibirLinhaCusto && (
                    <Grid item xs={12}>
                        <CustoRow
                            podeEditar={podeEditarCusto}
                            editando={editandoCusto}
                            valorExibicao={model.valorCusto}
                            valorDraft={valorCustoDraft}
                            onDraftChange={setValorCustoDraft}
                            onEditar={iniciarEdicaoCusto}
                            onSalvar={salvarCusto}
                            onCancelar={cancelarEdicaoCusto}
                            salvando={carregando}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

interface CustoRowProps {
    podeEditar: boolean;
    editando: boolean;
    valorExibicao: number;
    valorDraft: string;
    onDraftChange: (s: string) => void;
    onEditar: () => void;
    onSalvar: () => void | Promise<void>;
    onCancelar: () => void;
    salvando: boolean;
}

const CustoRow = (props: CustoRowProps) => {
    const classes = useStyles();
    const {
        podeEditar,
        editando,
        valorExibicao,
        valorDraft,
        onDraftChange,
        onEditar,
        onSalvar,
        onCancelar,
        salvando,
    } = props;

    return (
        <>
            <Typography variant="caption" color="textSecondary">
                Custo
            </Typography>
            {!editando ? (
                <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ gap: 4 }}>
                    <Typography
                        variant="body1"
                        className={classNames(classes.boldText)}>
                        {toCurrency(valorExibicao)}
                    </Typography>
                    {podeEditar && (
                        <IconButton
                            size="small"
                            aria-label="Editar custo"
                            onClick={onEditar}
                            disabled={salvando}>
                            <EditarIcon tipo="BUTTON" style={{ width: 20, height: 20, margin: 4 }} />
                        </IconButton>
                    )}
                </Box>
            ) : (
                <Box
                    component="form"
                    display="flex"
                    flexWrap="wrap"
                    alignItems="flex-start"
                    style={{ gap: 8 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        void onSalvar();
                    }}
                    mt={0.5}>
                    <Box flex="1" minWidth={160} maxWidth={280}>
                        <TextFieldSaurus
                            label="Valor de custo"
                            tipo="DECIMAL"
                            casasDecimais={2}
                            showStartAdornment
                            positivo
                            fullWidth
                            disabled={salvando}
                            allowSubmit
                            manterMascara
                            value={valorDraft}
                            onChange={(e) => {
                                onDraftChange(e.target.value);
                            }}
                        />
                    </Box>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={salvando}>
                        <OkIcon tipo="BUTTON_PRIMARY" style={{ margin: '6px 0' }} />
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        size="large"
                        disabled={salvando}
                        onClick={onCancelar}>
                        <CancelarIcon tipo="BUTTON" style={{ margin: '6px 0' }} />
                    </Button>
                </Box>
            )}
        </>
    );
};

const Item = (props: ItemProps) => {
    const classes = useStyles();

    return (
        <Grid item xs={props.xs || 12}>
            <Typography variant="caption" color="textSecondary">
                {props.label}
            </Typography>
            <Typography
                variant="body1"
                className={classNames(classes.boldText, {
                    [classes.dashText]: props.traço,
                })}>
                {props.conteudo}
            </Typography>
        </Grid>
    );
};
